import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  fetchClientIntegrations,
  fetchAlerts,
  toggleIntegration,
  syncIntegration,
  fetchClientNotionOverview,
  archiveNotionTask,
  unarchiveNotionTask,
  PLATFORM_META,
} from "@/lib/api";
import IntegrationCard from "@/components/IntegrationCard";
import AlertItem from "@/components/AlertItem";
import { toast } from "sonner";
import { CheckSquare, Inbox, FileText, Calendar, AlertCircle, ChevronRight, Archive, ArchiveRestore, Lock, Paperclip, RefreshCw } from "lucide-react";
import MinutaDrawer from "@/pages/client-tabs/MinutaDrawer";

const VERTICAL_COLORS = {
  "SEO/AEO/GEO": "bg-rose-50 text-rose-700 border-rose-200",
  "Comunicación y Posicionamiento": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Desarrollo de negocios": "bg-amber-50 text-amber-700 border-amber-200",
  "Tech": "bg-slate-100 text-slate-700 border-slate-200",
};

const PRIORITY_COLORS = {
  "Alta": "bg-rose-50 text-rose-700 border-rose-200",
  "Media": "bg-amber-50 text-amber-700 border-amber-200",
  "Baja": "bg-slate-50 text-slate-500 border-slate-200",
};

const STATUS_DOTS = {
  "En curso": "bg-sky-500",
  "Listo": "bg-emerald-500",
  "Pendiente": "bg-amber-500",
};

const STATUS_BADGE = {
  "En curso": "bg-sky-50 text-sky-700 border-sky-200",
  "Listo": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Pendiente": "bg-amber-50 text-amber-700 border-amber-200",
};

const fmtShortDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function OverviewTab({ clientId, clientName }) {
  const qc = useQueryClient();
  const [showArchivedTasks, setShowArchivedTasks] = useState(false);

  const { data: integrations = [] } = useQuery({
    queryKey: ["client-integrations", clientId],
    queryFn: () => fetchClientIntegrations(clientId),
  });

  const { data: notion, refetch: refetchNotion, isFetching: notionFetching } = useQuery({
    queryKey: ["client-notion", clientId],
    queryFn: () => fetchClientNotionOverview(clientId),
    // Kept short (1 min) so newly created minutas / projects in Notion surface
    // quickly. Also refetch on tab focus so switching back to the app pulls
    // fresh data without needing a manual refresh.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60_000,  // ~5 min autorefresh while tab is open
  });
  const { data: alerts = [] } = useQuery({
    queryKey: ["client-alerts", clientId],
    queryFn: () => fetchAlerts(clientId),
  });

  const toggle = useMutation({
    mutationFn: (integ) => toggleIntegration(clientId, integ.platform),
    onSuccess: (res) => {
      toast.success(res.connected ? `${PLATFORM_META[res.platform]?.name} conectado` : `Desconectado`);
      qc.invalidateQueries({ queryKey: ["client-integrations", clientId] });
    },
  });

  const sync = useMutation({
    mutationFn: (integ) => syncIntegration(clientId, integ.platform),
    onSuccess: () => {
      toast.success("Sincronizado correctamente");
      qc.invalidateQueries({ queryKey: ["client-integrations", clientId] });
    },
  });

  const archive = useMutation({
    mutationFn: (taskId) => archiveNotionTask(clientId, taskId),
    onSuccess: () => {
      toast.success("Tarea archivada");
      qc.invalidateQueries({ queryKey: ["client-notion", clientId] });
    },
    onError: () => toast.error("No se pudo archivar"),
  });

  const unarchive = useMutation({
    mutationFn: (taskId) => unarchiveNotionTask(clientId, taskId),
    onSuccess: () => {
      toast.success("Tarea restaurada");
      qc.invalidateQueries({ queryKey: ["client-notion", clientId] });
    },
    onError: () => toast.error("No se pudo restaurar"),
  });

  const archivedCount = notion?.archived_count || 0;

  return (
    <div className="space-y-8" data-testid="overview-tab">
      {/* Notion dump — 3 columnas: tareas del lado del cliente, pedidos a Narwhal, minutas */}
      <section data-testid="notion-overview">
        <div className="mb-3 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900 leading-tight">Tareas y minutas</h2>
            <p className="text-xs text-slate-500 mt-0.5">Sincronizado desde Notion · top 10 por sección</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {notion?.configured && (
              <button
                type="button"
                onClick={() => refetchNotion()}
                disabled={notionFetching}
                data-testid="notion-refresh-btn"
                title="Volver a consultar Notion ahora (por defecto refresca cada 5 min)"
                className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded border bg-white text-slate-600 border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <RefreshCw className={`h-3 w-3 ${notionFetching ? "animate-spin" : ""}`} />
                {notionFetching ? "Sincronizando…" : "Actualizar"}
              </button>
            )}
            {notion?.configured && (
              <button
                type="button"
                onClick={() => setShowArchivedTasks((v) => !v)}
                data-testid="toggle-archived-tasks"
                className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded border transition ${
                  showArchivedTasks
                    ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-800"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {showArchivedTasks ? <ArchiveRestore className="h-3 w-3" /> : <Archive className="h-3 w-3" />}
                {showArchivedTasks ? "Ocultar archivadas" : "Mostrar archivadas"}
                {archivedCount > 0 && (
                  <span className={`text-[10px] font-mono px-1 rounded ${showArchivedTasks ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>
                    {archivedCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <NotionPanel
            title={`Tareas pendientes${clientName ? ` · ${clientName}` : ""}`}
            subtitle="Cosas que tiene que resolver el cliente"
            icon={CheckSquare}
            tone="amber"
            testid="notion-panel-client-tasks"
            items={notion?.client_tasks || []}
            kind="task"
            configured={notion?.configured}
            message={notion?.message}
            showArchived={showArchivedTasks}
            onArchive={(id) => archive.mutate(id)}
            onUnarchive={(id) => unarchive.mutate(id)}
          />
          <NotionPanel
            title="Pedidos a Narwhal"
            subtitle="Lo que tenemos que entregar nosotros"
            icon={Inbox}
            tone="sky"
            testid="notion-panel-agency-tasks"
            items={notion?.agency_tasks || []}
            kind="task"
            configured={notion?.configured}
            message={notion?.message}
            showArchived={showArchivedTasks}
            onArchive={(id) => archive.mutate(id)}
            onUnarchive={(id) => unarchive.mutate(id)}
          />
          <NotionPanel
            title="Minutas"
            subtitle="Últimas reuniones registradas"
            icon={FileText}
            tone="violet"
            testid="notion-panel-minutas"
            items={notion?.minutas || []}
            kind="minuta"
            configured={notion?.configured}
            message={notion?.message}
          />
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ title, subtitle, small }) {
  return (
    <div className={small ? "mb-3" : "mb-4"}>
      <h2 className={`font-display ${small ? "text-base" : "text-lg"} font-semibold text-slate-900`}>{title}</h2>
      <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
    </div>
  );
}

function NotionPanel({ title, subtitle, icon: Icon, tone, testid, items, kind, configured, message, showArchived, onArchive, onUnarchive }) {
  const tones = {
    amber: { iconBg: "bg-amber-50", iconBorder: "border-amber-100", iconText: "text-amber-600" },
    sky: { iconBg: "bg-sky-50", iconBorder: "border-sky-100", iconText: "text-sky-600" },
    violet: { iconBg: "bg-violet-50", iconBorder: "border-violet-100", iconText: "text-violet-600" },
  };
  const t = tones[tone] || tones.amber;
  const allItems = items || [];
  // Para tareas: si showArchived=false escondemos las archivadas. Si showArchived=true mostramos solo archivadas.
  const list = kind === "task"
    ? (showArchived ? allItems.filter((i) => i.archived) : allItems.filter((i) => !i.archived))
    : allItems;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 min-h-[280px] flex flex-col" data-testid={testid}>
      <div className="flex items-start gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-md ${t.iconBg} border ${t.iconBorder} flex items-center justify-center shrink-0`}>
          <Icon className={`h-4 w-4 ${t.iconText}`} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900 leading-tight">{title}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        {configured && list.length > 0 && (
          <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
            {list.length}
          </span>
        )}
      </div>

      {!configured ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-6 bg-slate-50 rounded-md border border-dashed border-slate-200">
          <AlertCircle className="h-6 w-6 text-slate-400 mb-2" />
          <p className="text-xs font-medium text-slate-600">Notion no configurado</p>
          <p className="text-[11px] text-slate-400 mt-1 max-w-[220px]">
            {message || "Falta el token o el ID de la base de proyectos."}
          </p>
        </div>
      ) : list.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-6 text-slate-400">
          {kind === "minuta" ? (
            <>
              <FileText className="h-6 w-6 mb-1.5 opacity-50" />
              <p className="text-xs">Sin minutas registradas</p>
            </>
          ) : showArchived ? (
            <>
              <Archive className="h-6 w-6 mb-1.5 opacity-50" />
              <p className="text-xs">Sin tareas archivadas</p>
            </>
          ) : (
            <>
              <CheckSquare className="h-6 w-6 mb-1.5 opacity-50" />
              <p className="text-xs">Sin pendientes</p>
            </>
          )}
        </div>
      ) : (
        <ul className="space-y-2 overflow-y-auto max-h-[400px]" data-testid={`${testid}-list`}>
          {list.map((item) => kind === "minuta"
            ? <MinutaItem key={item.id} item={item} />
            : <TaskItem key={item.id} item={item} onArchive={onArchive} onUnarchive={onUnarchive} />
          )}
        </ul>
      )}
    </div>
  );
}

function TaskItem({ item, onArchive, onUnarchive }) {
  const verticalClass = VERTICAL_COLORS[item.vertical] || "bg-slate-100 text-slate-600 border-slate-200";
  const priorityClass = PRIORITY_COLORS[item.priority] || "bg-slate-50 text-slate-500 border-slate-200";
  const statusDot = STATUS_DOTS[item.status] || "bg-slate-400";
  const statusBadge = STATUS_BADGE[item.status] || "bg-slate-50 text-slate-600 border-slate-200";
  const isArchived = !!item.archived;
  // Bloqueado: Notion devuelve string "Si"/"Sí"/"Yes". Tratamos solo el caso afirmativo.
  const isBlocked = /^s[ií]$|^yes$/i.test((item.blocked || "").trim());

  return (
    <li
      className={`group border rounded-md p-2.5 relative ${
        isArchived ? "bg-slate-50/60 opacity-75 border-slate-200" :
        isBlocked ? "bg-rose-50/40 border-rose-200" :
        "border-slate-200"
      }`}
      data-testid={`notion-task-${item.id}`}
    >
      <div className="flex items-start gap-1.5">
        <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${statusDot} shrink-0`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className={`text-xs font-medium leading-snug line-clamp-2 ${isArchived ? "text-slate-500 line-through decoration-slate-300" : "text-slate-900"}`}>
              {item.title}
            </span>
            {isArchived ? (
              <button
                type="button"
                onClick={() => onUnarchive && onUnarchive(item.id)}
                title="Restaurar al tablero"
                data-testid={`unarchive-task-${item.id}`}
                className="text-slate-400 hover:text-emerald-600 shrink-0 transition opacity-60 group-hover:opacity-100"
              >
                <ArchiveRestore className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onArchive && onArchive(item.id)}
                title="Archivar (ocultar del tablero)"
                data-testid={`archive-task-${item.id}`}
                className="text-slate-300 hover:text-slate-700 shrink-0 transition opacity-0 group-hover:opacity-100"
              >
                <Archive className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {item.status && (
              <span className={`text-[9.5px] font-semibold px-1.5 py-0.5 rounded border ${statusBadge}`} data-testid={`task-status-${item.id}`}>
                {item.status}
              </span>
            )}
            {isBlocked && (
              <span className="text-[9.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200 inline-flex items-center gap-0.5" data-testid={`task-blocked-${item.id}`}>
                <Lock className="h-2.5 w-2.5" /> Bloqueado
              </span>
            )}
            {item.vertical && (
              <span className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded border ${verticalClass}`}>
                {item.vertical}
              </span>
            )}
            {item.priority && (
              <span className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded border ${priorityClass}`}>
                {item.priority}
              </span>
            )}
            {item.file?.url && (
              <a
                href={item.file.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`task-file-${item.id}`}
                className="text-[9.5px] font-medium px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 inline-flex items-center gap-0.5 max-w-[160px]"
                title={item.file.name || "Adjunto"}
              >
                <Paperclip className="h-2.5 w-2.5 shrink-0" />
                <span className="truncate">{item.file.name || "Adjunto"}</span>
              </a>
            )}
          </div>
          {(item.start_date || item.end_date) && (
            <div className="flex items-center gap-2 mt-1 text-[9.5px] text-slate-500">
              <Calendar className="h-2.5 w-2.5" />
              <span>{fmtShortDate(item.start_date)}</span>
              <span className="text-slate-300">→</span>
              <span>{fmtShortDate(item.end_date)}</span>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function MinutaItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border border-slate-200 rounded-md p-2.5 hover:bg-slate-50 transition" data-testid={`notion-minuta-${item.id}`}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-start gap-2 group w-full text-left"
        data-testid={`open-minuta-${item.id}`}
      >
        <FileText className="h-3.5 w-3.5 text-violet-500 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className="text-xs font-medium text-slate-900 group-hover:text-violet-600 line-clamp-2">{item.title}</span>
            <ChevronRight className="h-3 w-3 text-slate-300 group-hover:text-violet-500 mt-0.5 shrink-0" />
          </div>
          {item.date && (
            <div className="flex items-center gap-1 mt-0.5 text-[10px] text-slate-500">
              <Calendar className="h-2.5 w-2.5" />
              <span>{fmtShortDate(item.date)}</span>
            </div>
          )}
        </div>
      </button>
      <MinutaDrawer open={open} onOpenChange={setOpen} minuta={item} />
    </li>
  );
}
